// client/src/components/pages/TelegramBI/Analytics/DeliveryStatsChart.js
import React, { useState, useEffect } from 'react';
import { FaCheckCircle, FaTimesCircle, FaBolt, FaPaperPlane } from 'react-icons/fa';
import { useThemeTokens } from '../../../../utils/themeTokens';

const DeliveryStatsChart = ({ data, loading }) => {
  const t = useThemeTokens();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Reset animation on data change
  useEffect(() => {
    setMounted(false);
    const timer = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(timer);
  }, [data]);

  if (loading) {
    return (
      <div className="bg-surface rounded-lg border border-gray-200 p-5">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-gray-200 rounded w-40" />
          <div className="flex justify-center">
            <div className="w-44 h-44 bg-gray-100 rounded-full" />
          </div>
        </div>
      </div>
    );
  }

  const {
    total_executions = 0,
    successful_executions = 0,
    failed_executions = 0,
    cache_hits = 0,
  } = data || {};

  const total = total_executions;
  const successful = successful_executions;
  const failed = failed_executions;
  const cacheHits = cache_hits;

  // Donut chart parameters
  const size = 180;
  const strokeWidth = 24;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  // Calculate segment angles (proportions of circumference)
  const successPct = total > 0 ? successful / total : 0;
  const failedPct = total > 0 ? failed / total : 0;
  const cachePct = total > 0 ? cacheHits / total : 0;

  // Offset calculations for each segment
  // Segments go: success -> failed -> cache hits (remainder)
  const successLength = circumference * successPct;
  const failedLength = circumference * failedPct;
  const cacheLength = circumference * cachePct;

  const successOffset = mounted ? circumference - successLength : circumference;
  const failedOffset = mounted ? circumference - failedLength : circumference;
  const cacheOffset = mounted ? circumference - cacheLength : circumference;

  // Rotation for each segment so they appear sequentially
  const successRotation = -90; // Start at top
  const failedRotation = successRotation + successPct * 360;
  const cacheRotation = failedRotation + failedPct * 360;

  const segments = [
    {
      label: 'Successful',
      value: successful,
      pct: total > 0 ? ((successful / total) * 100).toFixed(1) : '0.0',
      color: '#10b981',
      icon: FaCheckCircle,
      dotClass: 'bg-emerald-500',
      length: successLength,
      offset: successOffset,
      rotation: successRotation,
    },
    {
      label: 'Failed',
      value: failed,
      pct: total > 0 ? ((failed / total) * 100).toFixed(1) : '0.0',
      color: '#ef4444',
      icon: FaTimesCircle,
      dotClass: 'bg-red-500',
      length: failedLength,
      offset: failedOffset,
      rotation: failedRotation,
    },
    {
      label: 'Cache Hits',
      value: cacheHits,
      pct: total > 0 ? ((cacheHits / total) * 100).toFixed(1) : '0.0',
      color: '#3b82f6',
      icon: FaBolt,
      dotClass: 'bg-blue-500',
      length: cacheLength,
      offset: cacheOffset,
      rotation: cacheRotation,
    },
  ];

  const hasNoData = total === 0;

  return (
    <div className="bg-surface rounded-lg border border-gray-200">
      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <FaPaperPlane className="text-[#0088cc]" />
          Delivery Stats
        </h3>
      </div>
      <div className="p-5">
        {hasNoData ? (
          <div className="py-12 text-center">
            <FaPaperPlane className="mx-auto text-3xl text-gray-300 mb-3" />
            <p className="text-sm text-gray-500">No delivery data available</p>
            <p className="text-xs text-gray-400 mt-1">
              Stats will appear once executions are recorded
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            {/* Donut Chart */}
            <div className="relative mb-6">
              <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                {/* Background circle */}
                <circle
                  cx={center}
                  cy={center}
                  r={radius}
                  fill="none"
                  stroke={t.gridSubtle}
                  strokeWidth={strokeWidth}
                />

                {/* Segments rendered in reverse order so first segment is on top */}
                {[...segments].reverse().map((seg, i) => {
                  if (seg.value === 0) return null;
                  return (
                    <circle
                      key={seg.label}
                      cx={center}
                      cy={center}
                      r={radius}
                      fill="none"
                      stroke={seg.color}
                      strokeWidth={strokeWidth}
                      strokeDasharray={`${seg.length} ${circumference - seg.length}`}
                      strokeDashoffset={seg.offset}
                      strokeLinecap="round"
                      transform={`rotate(${seg.rotation} ${center} ${center})`}
                      style={{
                        transition: 'stroke-dashoffset 1s cubic-bezier(0.4, 0, 0.2, 1)',
                        transitionDelay: `${(segments.length - 1 - i) * 200}ms`,
                      }}
                    />
                  );
                })}
              </svg>

              {/* Center text */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-bold text-gray-900">{total.toLocaleString()}</span>
                <span className="text-[11px] text-gray-500 font-medium">Total</span>
              </div>
            </div>

            {/* Legend */}
            <div className="w-full space-y-3">
              {segments.map((seg) => (
                <div key={seg.label} className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <span className={`w-2.5 h-2.5 rounded-full ${seg.dotClass}`} />
                    <span className="text-sm text-gray-700">{seg.label}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-gray-900">
                      {seg.value.toLocaleString()}
                    </span>
                    <span className="text-xs text-gray-500 w-12 text-right">{seg.pct}%</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Divider */}
            <div className="w-full border-t border-gray-100 my-4" />

            {/* Quick stats row */}
            <div className="w-full grid grid-cols-3 gap-3">
              <div className="bg-emerald-50 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-emerald-700">{segments[0].pct}%</div>
                <div className="text-[10px] text-emerald-600 font-medium uppercase tracking-wider">
                  Success
                </div>
              </div>
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-red-700">{segments[1].pct}%</div>
                <div className="text-[10px] text-red-600 font-medium uppercase tracking-wider">
                  Failed
                </div>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <div className="text-lg font-bold text-blue-700">{segments[2].pct}%</div>
                <div className="text-[10px] text-blue-600 font-medium uppercase tracking-wider">
                  Cached
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryStatsChart;
